import { usePropertyContext } from '@/context/propertyContext';

import type { RoomAvailabilityData } from '@/features/group-pricing/group-pricing-utils';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getPreviousDayUTC } from '../date-utils';
import { useGetRoomsRemainingQuery } from '../gql/_gen_/group-pricing.gql';

export interface RoomTypeAvailability {
	roomType: string;
	description: string;
	availability: Record<string, number>;
}

export interface FormattedRoomAvailability {
	room: string;
	subRows: FormattedRoomAvailability[];
	[date: string]: string | number | FormattedRoomAvailability[];
}

export interface RoomAvailabilityRow {
	roomType: string;
	description: string;
	dateValues: Record<string, string | number>;
}

export const useRoomAvailabilityData = () => {
	const form = useFormContext();

	const [checkIn, checkOut] = useWatch({
		control: form.control,
		name: ['check_in', 'check_out'],
	});

	const { property } = usePropertyContext();

	const { data, loading, error } = useGetRoomsRemainingQuery({
		skip: !property?.propertyId,
		variables: {
			startDate: checkIn,
			endDate: getPreviousDayUTC(checkOut),
			propertyId: property?.propertyId,
		},
	});

	// Get all available dates from the data
	const availableDates = useMemo(() => {
		if (!data?.getRoomsRemaining) return [];

		const dateSet = new Set<string>();
		data.getRoomsRemaining.forEach((room) => {
			if (room?.stay_date) {
				dateSet.add(room.stay_date);
			}
		});

		return Array.from(dateSet).sort();
	}, [data?.getRoomsRemaining]);

	// Process the raw data into room types with aggregated availability
	const roomTypeData: RoomTypeAvailability[] = useMemo(() => {
		if (!data?.getRoomsRemaining) return [];

		const roomMap = new Map<string, RoomTypeAvailability>();

		data.getRoomsRemaining.forEach((room) => {
			if (!room) return;

			const { rt_bed_type, rt_description, stay_date, rt_remaining } = room;

			if (!rt_bed_type || !stay_date) return;

			if (!roomMap.has(rt_bed_type)) {
				roomMap.set(rt_bed_type, {
					roomType: rt_bed_type,
					description: rt_description || '',
					availability: { [stay_date]: rt_remaining || 0 },
				});
			} else {
				const existingRoom = roomMap.get(rt_bed_type)!;
				existingRoom.availability[stay_date] = rt_remaining || 0;
			}
		});

		return Array.from(roomMap.values());
	}, [data?.getRoomsRemaining]);

	// Process for nested table display (room types with sub-rows)
	const formattedData: FormattedRoomAvailability[] = useMemo(() => {
		if (!data?.getRoomsRemaining) return [];

		const roomsMap = new Map<string, FormattedRoomAvailability>();

		data.getRoomsRemaining.forEach((room) => {
			if (!room) return;

			const { rt_bed_type, stay_date, rt_remaining, rt_description } = room;

			if (!rt_bed_type || !stay_date) return;

			// If the room type doesn't exist in the map yet, add it
			if (!roomsMap.has(rt_bed_type)) {
				roomsMap.set(rt_bed_type, {
					room: rt_bed_type,
					[stay_date]: rt_remaining || 0,
					subRows: [
						{
							room: rt_description || '',
							[stay_date]: rt_remaining || 0,
							subRows: [],
						},
					],
				});
			} else {
				// If the room type exists, update its data
				const currentValue = roomsMap.get(rt_bed_type)!;

				// Update the date total for the room type
				if (currentValue[stay_date]) {
					(currentValue[stay_date] as number) += rt_remaining || 0;
				} else {
					currentValue[stay_date] = rt_remaining || 0;
				}

				// Check if the description already exists in subRows
				const subRowIndex = currentValue.subRows.findIndex(
					(subRow) => subRow.room === rt_description,
				);

				if (subRowIndex >= 0) {
					// Update existing sub-row
					currentValue.subRows[subRowIndex][stay_date] = rt_remaining || 0;
				} else {
					// Add new sub-row
					currentValue.subRows.push({
						room: rt_description || '',
						[stay_date]: rt_remaining || 0,
						subRows: [],
					});
				}

				// Update the map
				roomsMap.set(rt_bed_type, currentValue);
			}
		});

		// Convert map to array
		return Array.from(roomsMap.values());
	}, [data?.getRoomsRemaining]);

	// Create simplified rows for modern table component
	const tableRows: RoomAvailabilityRow[] = useMemo(() => {
		if (!formattedData || !availableDates.length) return [];

		const result: RoomAvailabilityRow[] = [];

		// Add the main room types
		formattedData.forEach((roomType) => {
			const row: RoomAvailabilityRow = {
				roomType: roomType.room,
				description: '',
				dateValues: {},
			};

			// Add availability for each date
			availableDates.forEach((date) => {
				const value = roomType[date];
				row.dateValues[date] = typeof value === 'number' ? value : 0;
			});

			result.push(row);

			// Add sub-rows (room descriptions)
			roomType.subRows.forEach((subRow) => {
				const subRowData: RoomAvailabilityRow = {
					roomType: '',
					description: subRow.room,
					dateValues: {},
				};

				// Add availability for each date
				availableDates.forEach((date) => {
					const value = subRow[date];
					subRowData.dateValues[date] = typeof value === 'number' ? value : 0;
				});

				result.push(subRowData);
			});
		});

		return result;
	}, [formattedData, availableDates]);

	return {
		rawData: data?.getRoomsRemaining as RoomAvailabilityData[] | undefined,
		roomTypeData,
		formattedData,
		tableRows,
		availableDates,
		loading,
		error,
	};
};
